import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { recursoService } from '../services/api';
import { useAuth } from './AuthContext';

const PermisosContext = createContext(null);

export const PermisosProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [recursos, setRecursos] = useState([]);
  const [membresia, setMembresia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar recursos cuando el usuario está autenticado
  const cargarRecursos = useCallback(async () => {
    if (!isAuthenticated) {
      setRecursos([]);
      setMembresia(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await recursoService.getMisRecursos();
      setRecursos(response.data.recursos || []);
      setMembresia(response.data.membresia);
      setError(null);
    } catch (err) {
      console.error('Error al cargar recursos:', err);
      setError('Error al cargar permisos');
      setRecursos([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    cargarRecursos();
  }, [cargarRecursos]);

  /**
   * Verifica si el usuario tiene acceso a un recurso específico
   * @param {string} codigoRecurso - Código del recurso a verificar
   * @returns {boolean}
   */
  const tieneRecurso = useCallback((codigoRecurso) => {
    return recursos.includes(codigoRecurso);
  }, [recursos]);

  /**
   * Verifica si el usuario tiene acceso a ALGUNO de los recursos especificados
   * @param {string[]} codigosRecursos - Array de códigos de recursos
   * @returns {boolean}
   */
  const tieneAlgunRecurso = useCallback((codigosRecursos) => {
    return codigosRecursos.some(codigo => recursos.includes(codigo));
  }, [recursos]);

  /**
   * Verifica si el usuario tiene acceso a TODOS los recursos especificados
   * @param {string[]} codigosRecursos - Array de códigos de recursos
   * @returns {boolean}
   */
  const tieneTodosRecursos = useCallback((codigosRecursos) => {
    return codigosRecursos.every(codigo => recursos.includes(codigo));
  }, [recursos]);

  /**
   * Obtiene el nombre de la membresía actual
   * @returns {string|null}
   */
  const getNombreMembresia = useCallback(() => {
    return membresia?.nombre || null;
  }, [membresia]);

  /**
   * Recarga los recursos (útil después de un cambio de membresía)
   */
  const recargarRecursos = useCallback(() => {
    return cargarRecursos();
  }, [cargarRecursos]);

  const value = {
    // Estado
    recursos,
    membresia,
    loading,
    error,
    
    // Métodos de verificación
    tieneRecurso,
    tieneAlgunRecurso,
    tieneTodosRecursos,
    getNombreMembresia,
    
    // Utilidades
    recargarRecursos,
  };

  return (
    <PermisosContext.Provider value={value}>
      {children}
    </PermisosContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de permisos
 * @returns {Object} Contexto de permisos
 * 
 * @example
 * const { tieneRecurso, membresia } = usePermisos();
 * 
 * // Verificar un recurso
 * if (tieneRecurso('generar_pdf')) {
 *   // Mostrar botón de generar PDF
 * }
 * 
 * // En JSX con renderizado condicional
 * {tieneRecurso('crud_animales') && <MenuItem>Animales</MenuItem>}
 */
export const usePermisos = () => {
  const context = useContext(PermisosContext);
  if (!context) {
    throw new Error('usePermisos debe usarse dentro de PermisosProvider');
  }
  return context;
};

/**
 * Componente que renderiza sus hijos solo si el usuario tiene el recurso
 * @param {Object} props
 * @param {string|string[]} props.recurso - Código(s) del recurso requerido
 * @param {boolean} props.todos - Si true, requiere todos los recursos (default: false, requiere al menos uno)
 * @param {React.ReactNode} props.children - Contenido a renderizar si tiene acceso
 * @param {React.ReactNode} props.fallback - Contenido alternativo si no tiene acceso (opcional)
 * 
 * @example
 * <ConRecurso recurso="generar_pdf">
 *   <Button>Generar PDF</Button>
 * </ConRecurso>
 * 
 * <ConRecurso recurso={['crud_animales', 'crud_eventos']} todos={true}>
 *   <AdminPanel />
 * </ConRecurso>
 */
export const ConRecurso = ({ recurso, todos = false, children, fallback = null }) => {
  const { tieneRecurso, tieneAlgunRecurso, tieneTodosRecursos } = usePermisos();
  
  let tieneAcceso = false;
  
  if (Array.isArray(recurso)) {
    tieneAcceso = todos ? tieneTodosRecursos(recurso) : tieneAlgunRecurso(recurso);
  } else {
    tieneAcceso = tieneRecurso(recurso);
  }
  
  return tieneAcceso ? children : fallback;
};

export default PermisosContext;
